import { ConditionGroup, SlotFrameRef, SlotLayout, Spread } from 'editor/src/store/design/types';

/** The product count the buyer picked: the position of the active option, 1-based. */
export function getActiveSlotCount(layout: SlotLayout, conditionGroup: ConditionGroup | undefined): number | undefined {
  const condition = conditionGroup?.conditions[layout.conditionId];
  if (!condition) {
    return undefined;
  }
  const index = condition.options.findIndex((option) => option.id === condition.activeOptionId);
  return index === -1 ? undefined : index + 1;
}

function findSlotLayout(spread: Pick<Spread, 'slotLayouts'>, ref: SlotFrameRef): SlotLayout | undefined {
  return spread.slotLayouts?.find((item) => item.id === ref.layoutId);
}

/**
 * Layout ids that tagged layers point at but the spread no longer lists. Such layers count as
 * visible, so every frame would print on top of each other; the operation that recomputes
 * visibility reports them, keeping the lookups here free of side effects.
 */
export function getOrphanSlotLayoutIds(spread: Pick<Spread, 'slotLayouts' | 'pages'>): Set<string> {
  const orphans = new Set<string>();
  spread.pages.forEach((page) => {
    page.groups.media?.forEach((element) => {
      if (element.slotFrame && !findSlotLayout(spread, element.slotFrame)) {
        orphans.add(element.slotFrame.layoutId);
      }
    });
  });
  return orphans;
}

/** Ids of the frames the picked count does not show, per layout. */
function getInactiveFrameIds(spread: Pick<Spread, 'slotLayouts' | 'conditionGroup'>): Map<string, Set<string>> {
  const inactive = new Map<string, Set<string>>();
  spread.slotLayouts?.forEach((layout) => {
    const count = getActiveSlotCount(layout, spread.conditionGroup);
    inactive.set(
      layout.id,
      new Set(layout.frames.filter((frame) => count === undefined || !frame.counts.includes(count)).map((f) => f.id)),
    );
  });
  return inactive;
}

/** Whether a layer's frame is on the page for the picked count. A frame the layout no longer
 *  lists counts as off the page: nothing should print from a position that does not exist. */
export function isSlotFrameActive(spread: Pick<Spread, 'slotLayouts' | 'conditionGroup'>, ref: SlotFrameRef): boolean {
  const layout = findSlotLayout(spread, ref);
  if (!layout) {
    return true;
  }
  const count = getActiveSlotCount(layout, spread.conditionGroup);
  const frame = layout.frames.find((item) => item.id === ref.frameId);
  return !!frame && count !== undefined && frame.counts.includes(count);
}

/**
 * Layers sitting in a frame the picked count does not show. This is the second visibility rule
 * beside the condition tree: a frame's layers answer to the product's own picklist through the
 * tree, and to the count through their frame, so one layer can depend on both without being
 * listed under two conditions.
 */
export function getSlotFrameHiddenUuids(spread: Pick<Spread, 'slotLayouts' | 'conditionGroup' | 'pages'>): Set<number> {
  const hidden = new Set<number>();
  if (!spread.slotLayouts?.length) {
    return hidden;
  }
  const inactive = getInactiveFrameIds(spread);
  spread.pages.forEach((page) => {
    page.groups.media?.forEach((element) => {
      const ref = element.slotFrame;
      if (!ref) {
        return;
      }
      const layout = findSlotLayout(spread, ref);
      const frames = inactive.get(ref.layoutId);
      if (frames?.has(ref.frameId) || (layout && !layout.frames.some((frame) => frame.id === ref.frameId))) {
        hidden.add(element.uuid);
      }
    });
  });
  return hidden;
}
